/**
 * The one SWR fetcher. Twenty-four files used to define their own; twenty of
 * them were `fetch(url).then((res) => res.json())`, which hands a 401 or 500
 * body to the component as if it were data, so the page rendered "no results"
 * instead of an error. This version rejects on a non-2xx response the way the
 * settings pages always did, with `status` and the parsed body on the error
 * so a caller can read `error.info?.message` or branch on `error.status`.
 *
 *   const { data, error } = useSWR("/api/personas", fetcher);
 *
 * `envelopeFetcher` is for the `{ success, data, error }` envelope the agent
 * tools routes return: it also rejects when `success` is false (the error
 * still carries the HTTP status, 200 in that case), and resolves to the whole
 * payload. `envelopeDataFetcher` unwraps `.data` and rejects if it is absent.
 */
async function request(url, init) {
  const res = await fetch(url, init);
  const body = await res.json().catch(() => null);
  return { res, body };
}

function requestError(message, res, body) {
  const error = new Error(message);
  error.status = res.status;
  error.info = body;
  return error;
}

function httpMessage(res, body) {
  return body?.error || body?.message || `Request failed with status ${res.status}`;
}

/**
 * The error `fetcher` would have thrown for a non-2xx response, for a caller
 * that reads the body itself (a streamed response): the route's `error` text
 * as the message, `status` and the parsed body on it.
 */
export function httpError(res, body) {
  return requestError(httpMessage(res, body), res, body);
}

export async function fetcher(url, init) {
  const { res, body } = await request(url, init);
  if (!res.ok) throw requestError(httpMessage(res, body), res, body);
  return body;
}

export async function envelopeFetcher(url, init) {
  const { res, body } = await request(url, init);
  if (!res.ok) throw requestError(httpMessage(res, body), res, body);
  if (!body?.success) {
    throw requestError(body?.error || "Request failed.", res, body);
  }
  return body;
}

export async function envelopeDataFetcher(url, init) {
  const payload = await envelopeFetcher(url, init);
  if (!Object.hasOwn(payload, "data")) {
    throw new Error("Response envelope has no data.");
  }
  return payload.data;
}

/**
 * POSTs `body` (an empty object when absent) as JSON to a same-origin route
 * through `fetcher`, so the cookie session goes along and a non-2xx rejects
 * with the route's `error` text and `status`.
 *
 * `keepalive` lets the request outlive the page (sent as the tab closes). The
 * browser refuses a keepalive body over 64 KB: that rejects with no `status`.
 *
 * @param {string} url
 * @param {unknown} [body]
 * @param {{ signal?: AbortSignal, keepalive?: boolean }} [options]
 * @returns {Promise<any>} the parsed response body
 */
export function postJson(url, body, { signal, keepalive = false } = {}) {
  return fetcher(url, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
    signal,
    ...(keepalive ? { keepalive: true } : {}),
  });
}
